import {AnalyticsFactValue} from '../../models/vte';
import {
    SessionAnalyticsFact,
    SessionAnalyticsLeg,
    SessionAnalyticsStatus,
} from '../../models/vte/analytics-fact.model';

/** Customer-facing label when a verification capability was not expected at transaction time. */
export const ANALYTICS_FACT_NOT_ENABLED = 'Not Enabled';

export type ResolvedAnalyticsFact = {
    displayValue: string;
    confidenceLabel: string | null;
    passed: boolean | null;
};

export type AnalyticsFactResolveOptions = {
    analyticsKey?: string | null;
    outputFact?: string | null;
    /** When true, empty verification facts render as Verification Pending (if expected). */
    showPendingWhenEmpty?: boolean;
    /**
     * Whether this capability's provider was expected when the transaction/log was created.
     * - true  → empty verification may show Verification Pending
     * - false → empty verification shows Not Enabled (historical / never enabled)
     * - null/undefined → treat as unknown; Pending only when showPendingWhenEmpty
     */
    capabilityExpected?: boolean | null;
};

const STICKER_ANALYTICS_KEYS = new Set(['sticker_verification']);
const STICKER_OUTPUT_FACTS = new Set(['sticker_present', 'sticker_verification']);
const TOW_ANALYTICS_KEYS = new Set(['tow_vehicle_detection']);
const TOW_OUTPUT_FACTS = new Set(['tow_vehicle_detected', 'tow_vehicle_detection']);

export function isStickerVerificationFact(
    analyticsKey?: string | null,
    outputFact?: string | null,
): boolean {
    return STICKER_ANALYTICS_KEYS.has(analyticsKey || '')
        || STICKER_OUTPUT_FACTS.has(outputFact || '');
}

export function isTowVerificationFact(
    analyticsKey?: string | null,
    outputFact?: string | null,
): boolean {
    return TOW_ANALYTICS_KEYS.has(analyticsKey || '')
        || TOW_OUTPUT_FACTS.has(outputFact || '');
}

export function isVerificationFact(
    analyticsKey?: string | null,
    outputFact?: string | null,
): boolean {
    return isStickerVerificationFact(analyticsKey, outputFact)
        || isTowVerificationFact(analyticsKey, outputFact);
}

/**
 * Extract provider_id values from a VehicleTransaction.expected_provider_events snapshot
 * (or the VehicleLog copy under provider_metadata.vte_expected_provider_events).
 */
export function extractExpectedProviderIds(
    expectedProviderEvents: unknown,
): Set<string> {
    const ids = new Set<string>();
    if (!Array.isArray(expectedProviderEvents)) {
        return ids;
    }
    for (const item of expectedProviderEvents) {
        if (!item || typeof item !== 'object') {
            continue;
        }
        const providerId = String((item as Record<string, unknown>)['provider_id'] || '').trim();
        if (providerId) {
            ids.add(providerId);
        }
    }
    return ids;
}

export function isProviderExpected(
    expectedProviderEvents: unknown,
    providerId?: string | null,
): boolean {
    const provider = (providerId || '').trim();
    if (!provider) {
        return false;
    }
    return extractExpectedProviderIds(expectedProviderEvents).has(provider);
}

export function resolveAnalyticsFactValue(
    value: AnalyticsFactValue | null | undefined,
    confidence?: number | null,
    options: AnalyticsFactResolveOptions = {},
): ResolvedAnalyticsFact | null {
    const {
        analyticsKey,
        outputFact,
        showPendingWhenEmpty = false,
        capabilityExpected,
    } = options;
    const verification = isVerificationFact(analyticsKey, outputFact);

    if (value === null || value === undefined) {
        return emptyVerificationOrNull(verification, showPendingWhenEmpty, capabilityExpected);
    }

    let rawValue: boolean | number | string | null = null;
    let resolvedConfidence = confidence ?? null;
    let passed: boolean | null = null;

    if (typeof value === 'boolean' || typeof value === 'number' || typeof value === 'string') {
        rawValue = value;
        if (typeof value === 'boolean') {
            passed = value;
        }
    } else if (typeof value === 'object') {
        rawValue = value.value ?? (typeof value.passed === 'boolean' ? value.passed : null);
        if (typeof value.confidence === 'number') {
            resolvedConfidence = value.confidence;
        }
        if (typeof value.passed === 'boolean') {
            passed = value.passed;
        }
        // Backend enrich facts may only set received:true before snapshot normalization.
        if (passed === null && (value as { received?: unknown }).received === true) {
            passed = true;
            rawValue = rawValue ?? true;
        }
    }

    if (rawValue === null || rawValue === undefined || rawValue === '') {
        if (typeof passed === 'boolean') {
            return verificationDisplay(passed, resolvedConfidence, analyticsKey, outputFact);
        }
        return emptyVerificationOrNull(verification, showPendingWhenEmpty, capabilityExpected);
    }

    if (verification) {
        if (typeof passed !== 'boolean') {
            if (typeof rawValue === 'boolean') {
                passed = rawValue;
            } else if (typeof rawValue === 'string') {
                const lowered = rawValue.toLowerCase();
                if (lowered === 'true' || lowered === 'false') {
                    passed = lowered === 'true';
                } else if (lowered === 'yes' || lowered === 'no') {
                    passed = lowered === 'yes';
                } else {
                    // Non-empty label (e.g. "Fleet Sticker") means present.
                    passed = true;
                }
            } else {
                passed = Boolean(rawValue);
            }
        }
        return verificationDisplay(passed === true, resolvedConfidence, analyticsKey, outputFact);
    }

    if (typeof rawValue === 'boolean') {
        return {
            displayValue: rawValue ? 'Yes' : 'No',
            confidenceLabel: formatConfidence(resolvedConfidence),
            passed: rawValue,
        };
    }

    if (typeof passed !== 'boolean' && typeof rawValue === 'string') {
        const lowered = rawValue.toLowerCase();
        if (lowered === 'true' || lowered === 'false') {
            passed = lowered === 'true';
        }
    }

    return {
        displayValue: String(rawValue),
        confidenceLabel: formatConfidence(resolvedConfidence),
        passed,
    };
}

/**
 * Map backend session_analytics leg status → customer display text.
 * Frontend owns wording; backend returns semantic status only.
 */
export function resolveSessionAnalyticsLeg(
    leg: SessionAnalyticsLeg | null | undefined,
    analyticsKey?: string | null,
    outputFact?: string | null,
): ResolvedAnalyticsFact | null {
    if (!leg || !leg.status) {
        return null;
    }
    const status = leg.status as SessionAnalyticsStatus;
    const confidenceLabel = formatConfidence(
        typeof leg.confidence === 'number' ? leg.confidence : null,
    );

    switch (status) {
        case 'VERIFIED':
            return verificationDisplay(true, leg.confidence ?? null, analyticsKey, outputFact);
        case 'MISSING':
            return verificationDisplay(false, leg.confidence ?? null, analyticsKey, outputFact);
        case 'PENDING':
            return {
                displayValue: 'Verification Pending',
                confidenceLabel: null,
                passed: null,
            };
        case 'NOT_ENABLED':
            return {
                displayValue: ANALYTICS_FACT_NOT_ENABLED,
                confidenceLabel: null,
                passed: null,
            };
        case 'PRESENT': {
            const value = leg.value;
            if (value === null || value === undefined || value === '') {
                return null;
            }
            if (typeof value === 'boolean') {
                return {
                    displayValue: value ? 'Yes' : 'No',
                    confidenceLabel,
                    passed: value,
                };
            }
            return {
                displayValue: String(value),
                confidenceLabel,
                passed: null,
            };
        }
        case 'UNKNOWN':
        default:
            return null;
    }
}

export function resolveSessionAnalyticsFact(
    fact: SessionAnalyticsFact | null | undefined,
    analyticsKey?: string | null,
    outputFact?: string | null,
): {
    overall: ResolvedAnalyticsFact | null;
    entry: ResolvedAnalyticsFact | null;
    exit: ResolvedAnalyticsFact | null;
} | null {
    if (!fact?.overall) {
        return null;
    }
    const key = analyticsKey || fact.analytics_key || '';
    return {
        overall: resolveSessionAnalyticsLeg(fact.overall, key, outputFact),
        entry: resolveSessionAnalyticsLeg(fact.entry, key, outputFact),
        exit: resolveSessionAnalyticsLeg(fact.exit, key, outputFact),
    };
}

function emptyVerificationOrNull(
    verification: boolean,
    showPendingWhenEmpty: boolean,
    capabilityExpected?: boolean | null,
): ResolvedAnalyticsFact | null {
    if (!verification) {
        return null;
    }
    if (capabilityExpected === false) {
        return {
            displayValue: ANALYTICS_FACT_NOT_ENABLED,
            confidenceLabel: null,
            passed: null,
        };
    }
    if (showPendingWhenEmpty) {
        return pendingOrNull(true);
    }
    return null;
}

function pendingOrNull(showPending: boolean): ResolvedAnalyticsFact | null {
    if (!showPending) {
        return null;
    }
    return {
        displayValue: 'Verification Pending',
        confidenceLabel: null,
        passed: null,
    };
}

function verificationDisplay(
    passed: boolean,
    confidence: number | null,
    analyticsKey?: string | null,
    outputFact?: string | null,
): ResolvedAnalyticsFact {
    if (isStickerVerificationFact(analyticsKey, outputFact)) {
        return {
            displayValue: passed ? 'Sticker Verified' : 'Sticker Missing',
            confidenceLabel: formatConfidence(confidence),
            passed,
        };
    }
    if (isTowVerificationFact(analyticsKey, outputFact)) {
        return {
            displayValue: passed ? 'Tow Detected' : 'No Tow Vehicle',
            confidenceLabel: formatConfidence(confidence),
            passed,
        };
    }
    return {
        displayValue: passed ? 'Yes' : 'No',
        confidenceLabel: formatConfidence(confidence),
        passed,
    };
}

function formatConfidence(confidence: number | null): string | null {
    if (confidence === null || confidence === undefined || Number.isNaN(confidence)) {
        return null;
    }
    const percent = confidence <= 1 ? Math.round(confidence * 100) : Math.round(confidence);
    return `${percent}%`;
}
